/**
 * Flow map drawing geometry: where each node's box sits, where its handles
 * are, and the SVG path of an arrow between two handles.
 *
 * React-free and deterministic, so the static picture (server), the live
 * canvas (client) and the auto layout compute the same numbers. Every box is
 * built from FLOW_NODE_SIZE (groups use their own `size`), which is also the
 * width the live canvas renders a node at, so a picture drawn from here lines
 * up with the canvas that replaces it.
 *
 * A child node's stored position is relative to its group; every box returned
 * here is in absolute canvas units.
 */
import {
  FLOW_LIMITS,
  FLOW_NODE_SIZE,
  type FlowDirection,
  type FlowEdge,
  type FlowEdgeStyle,
  type FlowHandle,
  type FlowMapDoc,
  type FlowNode,
  type FlowPoint,
  type FlowSize,
} from "../model.js";

export interface FlowRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** One decimal place and never -0, the same rounding the model applies to stored coordinates. */
export function round1(n: number): number {
  const r = Math.round(n * 10) / 10;
  return r === 0 ? 0 : r;
}

/** A coordinate clamped to the document's range and rounded like a stored one. */
export function clampCoord(n: number): number {
  return round1(Math.max(-FLOW_LIMITS.coord, Math.min(FLOW_LIMITS.coord, n)));
}

/** The drawn size of a node: its kind's box, or the group's own size. */
export function nodeSize(node: FlowNode): FlowSize {
  if (node.kind === "group") return node.size ?? FLOW_NODE_SIZE.group;
  return FLOW_NODE_SIZE[node.kind] ?? FLOW_NODE_SIZE.step;
}

function boxOf(node: FlowNode, parent: FlowNode | undefined): FlowRect {
  const { width, height } = nodeSize(node);
  if (!parent) return { x: node.position.x, y: node.position.y, width, height };
  return { x: round1(parent.position.x + node.position.x), y: round1(parent.position.y + node.position.y), width, height };
}

/** The node's box in absolute canvas units. */
export function nodeBox(doc: FlowMapDoc, node: FlowNode): FlowRect {
  const parent = node.parentId ? doc.nodes.find((n) => n.id === node.parentId && n.kind === "group") : undefined;
  return boxOf(node, parent);
}

/** Every node's absolute box by id, in one pass (what the picture and the bounds read). */
export function nodeBoxes(doc: FlowMapDoc): Map<string, FlowRect> {
  const groups = new Map<string, FlowNode>();
  for (const n of doc.nodes) if (n.kind === "group") groups.set(n.id, n);
  const out = new Map<string, FlowRect>();
  for (const n of doc.nodes) out.set(n.id, boxOf(n, n.parentId ? groups.get(n.parentId) : undefined));
  return out;
}

/** The union of every node box, grown by `pad` on each side. An empty map is a zero rect at the origin. */
export function flowBounds(doc: FlowMapDoc, pad = 0): FlowRect {
  if (!doc.nodes.length) return { x: 0, y: 0, width: 0, height: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const b of nodeBoxes(doc).values()) {
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
  }
  return {
    x: round1(minX - pad),
    y: round1(minY - pad),
    width: round1(maxX - minX + 2 * pad),
    height: round1(maxY - minY + 2 * pad),
  };
}

// ---------------------------------------------------------------------------
// Handles
// ---------------------------------------------------------------------------

/** Where an arrow leaves and enters when it names no handle: TB bottom to top, LR right to left. */
export function defaultHandles(direction: FlowDirection | undefined): { source: FlowHandle; target: FlowHandle } {
  return direction === "LR" ? { source: "r", target: "l" } : { source: "b", target: "t" };
}

export function edgeHandles(doc: FlowMapDoc, edge: FlowEdge): { source: FlowHandle; target: FlowHandle } {
  const d = defaultHandles(doc.direction);
  return { source: edge.sourceHandle ?? d.source, target: edge.targetHandle ?? d.target };
}

/** The midpoint of the box side a handle sits on. */
export function handlePoint(rect: FlowRect, handle: FlowHandle): FlowPoint {
  switch (handle) {
    case "t":
      return { x: round1(rect.x + rect.width / 2), y: rect.y };
    case "r":
      return { x: round1(rect.x + rect.width), y: round1(rect.y + rect.height / 2) };
    case "b":
      return { x: round1(rect.x + rect.width / 2), y: round1(rect.y + rect.height) };
    case "l":
      return { x: rect.x, y: round1(rect.y + rect.height / 2) };
  }
}

// ---------------------------------------------------------------------------
// Arrow paths
// ---------------------------------------------------------------------------

/** The outward direction of each handle. */
const NORMAL: Readonly<Record<FlowHandle, FlowPoint>> = {
  t: { x: 0, y: -1 },
  r: { x: 1, y: 0 },
  b: { x: 0, y: 1 },
  l: { x: -1, y: 0 },
};

const fmt = (p: FlowPoint): string => `${round1(p.x)},${round1(p.y)}`;

/** Stub length an orthogonal arrow travels straight out of (and into) a handle before it turns. */
const ELBOW_STUB = 20;
/** Corner radius of an orthogonal arrow, the same as the live canvas's smooth-step edges. */
const ELBOW_RADIUS = 10;
/** Sideways detour when a back arrow would otherwise run straight through the nodes it joins. */
const ELBOW_DETOUR = 150;

function curvedPath(from: FlowPoint, fromHandle: FlowHandle, to: FlowPoint, toHandle: FlowHandle) {
  // Push each control point out along its handle's normal, by at least 40 and by
  // half the distance to the other end along that axis (React Flow's bezier feel).
  const push = (a: FlowPoint, b: FlowPoint, h: FlowHandle): FlowPoint => {
    const n = NORMAL[h];
    const along = n.x === 0 ? b.y - a.y : b.x - a.x;
    const k = Math.max(40, 0.5 * Math.abs(along));
    return { x: a.x + n.x * k, y: a.y + n.y * k };
  };
  const c1 = push(from, to, fromHandle);
  const c2 = push(to, from, toHandle);
  // The curve's point at t = 0.5.
  const labelX = round1(0.125 * from.x + 0.375 * c1.x + 0.375 * c2.x + 0.125 * to.x);
  const labelY = round1(0.125 * from.y + 0.375 * c1.y + 0.375 * c2.y + 0.125 * to.y);
  return { d: `M${fmt(from)} C${fmt(c1)} ${fmt(c2)} ${fmt(to)}`, labelX, labelY };
}

/**
 * The corner points of an orthogonal arrow. The common case (handles facing
 * each other with the target ahead) is the three-segment elbow: out along the
 * source normal to the midline, across, and into the target. Perpendicular
 * handles meet at one corner. Anything else leaves and enters through short
 * stubs and routes around.
 */
function elbowPoints(s: FlowPoint, sh: FlowHandle, t: FlowPoint, th: FlowHandle): FlowPoint[] {
  const ns = NORMAL[sh];
  const nt = NORMAL[th];
  const sVertical = ns.x === 0;
  const tVertical = nt.x === 0;

  if (sVertical === tVertical) {
    // Both handles on the same axis. Work in (main, cross) coordinates so one branch covers TB and LR.
    const main = (p: FlowPoint): number => (sVertical ? p.y : p.x);
    const cross = (p: FlowPoint): number => (sVertical ? p.x : p.y);
    const pt = (m: number, c: number): FlowPoint => (sVertical ? { x: c, y: m } : { x: m, y: c });
    const sn = sVertical ? ns.y : ns.x;
    const tn = sVertical ? nt.y : nt.x;
    const sm = main(s);
    const tm = main(t);
    const sc = cross(s);
    const tc = cross(t);
    if (sn !== tn) {
      if ((tm - sm) * sn > 0) {
        const mid = (sm + tm) / 2;
        return [s, pt(mid, sc), pt(mid, tc), t];
      }
      // The target sits behind the source: step out, go around, step back in.
      const sOut = sm + sn * ELBOW_STUB;
      const tOut = tm + tn * ELBOW_STUB;
      const around = Math.abs(sc - tc) < 1 ? sc + ELBOW_DETOUR : (sc + tc) / 2;
      return [s, pt(sOut, sc), pt(sOut, around), pt(tOut, around), pt(tOut, tc), t];
    }
    // Both handles face the same way: a U through the outermost stub line.
    const edge = sn > 0 ? Math.max(sm, tm) + ELBOW_STUB : Math.min(sm, tm) - ELBOW_STUB;
    return [s, pt(edge, sc), pt(edge, tc), t];
  }

  // Perpendicular handles: a single corner when it lies ahead of both handles.
  const corner: FlowPoint = sVertical ? { x: s.x, y: t.y } : { x: t.x, y: s.y };
  const aheadOfSource = (corner.x - s.x) * ns.x + (corner.y - s.y) * ns.y > 0;
  const aheadOfTarget = (corner.x - t.x) * nt.x + (corner.y - t.y) * nt.y > 0;
  if (aheadOfSource && aheadOfTarget) return [s, corner, t];
  const sOut: FlowPoint = { x: s.x + ns.x * ELBOW_STUB, y: s.y + ns.y * ELBOW_STUB };
  const tOut: FlowPoint = { x: t.x + nt.x * ELBOW_STUB, y: t.y + nt.y * ELBOW_STUB };
  const turn: FlowPoint = sVertical ? { x: tOut.x, y: sOut.y } : { x: sOut.x, y: tOut.y };
  return [s, sOut, turn, tOut, t];
}

/** Drop repeated points and the middle of straight runs, so every remaining inner point is a real corner. */
function simplify(points: FlowPoint[]): FlowPoint[] {
  const out: FlowPoint[] = [];
  for (const p of points) {
    const last = out[out.length - 1];
    if (last && Math.abs(last.x - p.x) < 0.05 && Math.abs(last.y - p.y) < 0.05) continue;
    out.push(p);
  }
  for (let i = out.length - 2; i >= 1; i--) {
    const a = out[i - 1]!;
    const b = out[i]!;
    const c = out[i + 1]!;
    const straight =
      (Math.abs(a.x - b.x) < 0.05 && Math.abs(b.x - c.x) < 0.05) || (Math.abs(a.y - b.y) < 0.05 && Math.abs(b.y - c.y) < 0.05);
    if (straight) out.splice(i, 1);
  }
  return out;
}

const dist = (a: FlowPoint, b: FlowPoint): number => Math.hypot(b.x - a.x, b.y - a.y);

function towards(from: FlowPoint, to: FlowPoint, by: number): FlowPoint {
  const d = dist(from, to) || 1;
  return { x: from.x + ((to.x - from.x) / d) * by, y: from.y + ((to.y - from.y) / d) * by };
}

/** The point halfway along a polyline, where the arrow's label sits. */
function midpointOf(points: FlowPoint[]): FlowPoint {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += dist(points[i - 1]!, points[i]!);
  let left = total / 2;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    const seg = dist(a, b);
    if (seg >= left && seg > 0) return towards(a, b, left);
    left -= seg;
  }
  return points[points.length - 1] ?? { x: 0, y: 0 };
}

function orthogonalPath(from: FlowPoint, fromHandle: FlowHandle, to: FlowPoint, toHandle: FlowHandle) {
  const pts = simplify(elbowPoints(from, fromHandle, to, toHandle));
  // Two ends closer than the rounding step collapse to one point: keep a (zero-length) segment.
  if (pts.length < 2) pts.push(to);
  const first = pts[0] ?? from;
  let d = `M${fmt(first)}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    const c = pts[i + 1]!;
    const bend = Math.min(ELBOW_RADIUS, dist(a, b) / 2, dist(b, c) / 2);
    d += ` L${fmt(towards(b, a, bend))} Q${fmt(b)} ${fmt(towards(b, c, bend))}`;
  }
  if (pts.length > 1) d += ` L${fmt(pts[pts.length - 1]!)}`;
  const mid = midpointOf(pts);
  return { d, labelX: round1(mid.x), labelY: round1(mid.y) };
}

/**
 * The SVG path of one arrow and where its label sits. `curved` (the default)
 * is a cubic bezier; `orthogonal` is an elbow with rounded corners. Numbers are
 * rounded to one decimal so a server render and a client render print the same string.
 */
export function edgePath(
  from: FlowPoint,
  fromHandle: FlowHandle,
  to: FlowPoint,
  toHandle: FlowHandle,
  style: FlowEdgeStyle = "curved",
): { d: string; labelX: number; labelY: number } {
  return style === "orthogonal" ? orthogonalPath(from, fromHandle, to, toHandle) : curvedPath(from, fromHandle, to, toHandle);
}
